import { environment } from 'environment';
import type { ApiConfig } from './essentials';
import { NetworkError } from './network';

type StrapiConfig = RequestInit & {
  params?: {
    [key: string]: string | number | boolean | object;
  };
  responseType?: 'json' | 'text' | 'blob' | 'formData';
};

class Strapi {
  private static instance?: Strapi;
  private readonly base: string;
  private readonly key: string;

  private constructor() {
    this.base = environment.urls.cms;
    this.key = environment.strapi.read;
  }

  async get<T = unknown>(url: string, config: StrapiConfig = {}): Promise<[T, Response]> {
    const [uri, conf, responseType] = await this.makeOptions(url, config, 'GET');

    const res = await fetch(uri, conf);

    if (!res.ok) throw new NetworkError(await res.text(), 'GET', res);
    return [await res[responseType](), res];
  }

  async post<R = unknown, T = unknown>(url: string, data: R, config: StrapiConfig = {}): Promise<[T, Response]> {
    const [uri, conf, responseType] = await this.makeOptions(url, { ...config, data }, 'POST');

    const res = await fetch(uri, conf);

    if (!res.ok) throw new NetworkError(await res.text(), 'POST', res);
    return [await res[responseType](), res];
  }

  async delete<R = unknown, T = unknown>(url: string, data?: R, config: StrapiConfig = {}): Promise<[T, Response]> {
    const [uri, conf, responseType] = await this.makeOptions(url, { ...config, data }, 'DELETE');

    const res = await fetch(uri, conf);

    if (!res.ok) throw new NetworkError(await res.text(), 'DELETE', res);
    return [await res[responseType](), res];
  }

  public static getInstance() {
    if (!this.instance) this.instance = new Strapi();
    return this.instance;
  }

  async makeOptions(
    url: string,
    origConfig: Omit<ApiConfig, 'params'> & {
      params?: {
        [key: string]: string | number | boolean | object;
      };
      data?: unknown;
    },
    method: 'GET' | 'POST' | 'DELETE',
  ): Promise<[URL, ApiConfig, ApiConfig['responseType']]> {
    const { params, responseType = 'json', ...configRest } = origConfig;

    const uri = new URL(`${this.base}${url}`);
    if (params) uri.search = (await import('qs')).stringify(params, { encodeValuesOnly: true });

    const config = {
      ...configRest,
      headers: { 'Content-Type': 'application/json', Authorization: `bearer ${this.key}` },
      method,
      body: configRest.data ? JSON.stringify(configRest.data) : undefined,
    };

    return [uri, config, responseType];
  }
}

const strapi = Strapi.getInstance();

export { strapi };
